import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkinned } from "three/addons/utils/SkeletonUtils.js";

// Rigged, animated soldier models. Current pack: KayKit Character Pack
// (CC0) under public/models/soldiers/ — see MANIFEST.md there for clip
// names and licensing. Swapping in a different rigged GLB only requires its
// clip names to appear in CLIP_CANDIDATES below.

type AnimName = "idle" | "walk" | "shoot" | "die";

// First matching clip name wins, so packs with different naming conventions
// (KayKit, Kenney, Quaternius, Mixamo exports) all resolve without code
// changes elsewhere.
const CLIP_CANDIDATES: Record<AnimName, string[]> = {
  idle: ["Idle_Combat", "Idle", "idle", "CharacterArmature|Idle"],
  walk: ["Walking_A", "Walking_B", "walk", "Walk", "CharacterArmature|Walk"],
  shoot: ["1H_Ranged_Shoot", "2H_Ranged_Shoot", "holding-right-shoot", "Shoot", "CharacterArmature|Shoot"],
  die: ["Death_A", "Death_B", "die", "Death", "CharacterArmature|Death"],
};

const DEFAULT_SOLDIER_PATH = "/models/soldiers/Skeleton_Minion.glb";
// KayKit rigs are ~2.17 units tall at scale 1; 0.85 lands at human height.
export const SOLDIER_MODEL_SCALE = 0.85;

export type SoldierAsset = {
  clips: THREE.AnimationClip[];
  template: THREE.Group;
};

export async function loadSoldierAsset(path = DEFAULT_SOLDIER_PATH): Promise<SoldierAsset> {
  const loader = new GLTFLoader();
  const gltf = await loader.loadAsync(path);
  return { clips: gltf.animations, template: gltf.scene };
}

// Skinned meshes can't be deep-cloned with Object3D.clone — bones would keep
// pointing at the source skeleton. SkeletonUtils.clone handles the remap;
// materials are then cloned so per-instance emissive tinting works.
function cloneScene(src: THREE.Group): THREE.Group {
  const cloned = cloneSkinned(src);
  cloned.traverse((obj) => {
    if ((obj as THREE.Mesh).isMesh) {
      const m = obj as THREE.Mesh;
      if (Array.isArray(m.material)) {
        m.material = (m.material as THREE.Material[]).map((mat) => mat.clone());
      } else {
        m.material = (m.material as THREE.Material).clone();
      }
    }
  });
  return cloned as THREE.Group;
}

function resolveClip(clips: THREE.AnimationClip[], name: AnimName): THREE.AnimationClip | null {
  for (const candidate of CLIP_CANDIDATES[name]) {
    const clip = clips.find((c) => c.name === candidate);
    if (clip) return clip;
  }
  return null;
}

export class SoldierInstance {
  readonly group = new THREE.Group();
  private mixer: THREE.AnimationMixer;
  private actions: Partial<Record<AnimName, THREE.AnimationAction>> = {};
  private currentAnim: AnimName = "idle";
  private dead = false;
  private hitFlashLeft = 0;

  constructor(asset: SoldierAsset, emissive: THREE.Color, tag: Record<string, boolean>, scale = SOLDIER_MODEL_SCALE) {
    const scene = cloneScene(asset.template);
    scene.scale.setScalar(scale);

    scene.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        const m = obj as THREE.Mesh;
        Object.assign(m.userData, tag);
        const mat = m.material as THREE.MeshStandardMaterial;
        mat.emissive = emissive.clone();
        mat.emissiveIntensity = 1.8;
      }
    });

    this.group.add(scene);
    this.mixer = new THREE.AnimationMixer(scene);

    for (const name of ["idle", "walk", "shoot", "die"] as const) {
      const clip = resolveClip(asset.clips, name);
      if (clip) this.actions[name] = this.mixer.clipAction(clip);
    }

    // Desync mixers so a squad doesn't march in lockstep
    this.actions.idle?.play();
    this.mixer.update(Math.random() * 2);
  }

  private crossFade(to: AnimName, duration = 0.25): void {
    if (this.currentAnim === to) return;
    const prev = this.actions[this.currentAnim];
    const next = this.actions[to];
    if (!next) return;
    if (prev) {
      next.reset().play();
      prev.crossFadeTo(next, duration, true);
    } else {
      next.reset().play();
    }
    this.currentAnim = to;
  }

  setMoving(moving: boolean): void {
    if (this.dead) return;
    // Movement wins over the shoot pose
    if (moving) this.crossFade("walk");
    else if (this.currentAnim === "walk") this.crossFade("idle");
  }

  flashHit(): void {
    // Brief emissive spike — visual hit confirm (ticked down in update)
    this.hitFlashLeft = 0.08;
    for (const mat of this.getMaterials()) mat.emissiveIntensity = 4.0;
  }

  private getMaterials(): THREE.MeshStandardMaterial[] {
    const out: THREE.MeshStandardMaterial[] = [];
    this.group.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) out.push((obj as THREE.Mesh).material as THREE.MeshStandardMaterial);
    });
    return out;
  }

  setShooting(shooting: boolean): void {
    if (this.dead || this.currentAnim === "walk") return;
    this.crossFade(shooting ? "shoot" : "idle", 0.15);
  }

  die(): void {
    if (this.dead) return;
    this.dead = true;
    const action = this.actions["die"];
    if (action) {
      action.reset();
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      const prev = this.actions[this.currentAnim];
      if (prev) prev.crossFadeTo(action, 0.2, true);
      action.play();
    }
    this.currentAnim = "die";
    // Kill emissive so dead bodies don't glow
    for (const mat of this.getMaterials()) mat.emissiveIntensity = 0;
  }

  update(dt: number): void {
    if (this.hitFlashLeft > 0) {
      this.hitFlashLeft -= dt;
      if (this.hitFlashLeft <= 0) {
        for (const mat of this.getMaterials()) mat.emissiveIntensity = this.dead ? 0 : 1.8;
      }
    }
    this.mixer.update(dt);
  }
}
